import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Column, Row, Spacer } from "../../../../components/Layout/Layout";
import { ResponseCenterAlarmCodeRuleDetails } from "../../../../core/api/responsecenters/types";
import useTranslations from "../../../../core/i18n/useTranslations";
import MultiCodeInput from "../../../../ui-lib/components/MultiInput/MultiCodeInput";
import { CheckboxControl } from "../AlarmHandling/Controls/CheckboxControl";
import TextInput from "../../../../ui-lib/components/Inputs/TextInput";
import PrimeModal from "../../../../ui-lib/components/PrimeModal/PrimeModal";
import LoadingSpinner from "../../../../ui-lib/components/Loading/LoadingSpinner";

export interface AlarmCodeRuleFormProps {
  isOpen: boolean;
  labels: {
    title: string;
    description: string;
    submit: string;
  };
  defaultValues?: ResponseCenterAlarmCodeRuleDetails;
  isLoading?: boolean;
  onSubmit: (values: ResponseCenterAlarmCodeRuleDetails) => void;
  onClose: () => void;
}

export const AlarmCodeRuleForm = ({
  isOpen,
  labels,
  defaultValues,
  isLoading,
  onSubmit,
  onClose,
}: AlarmCodeRuleFormProps) => {
  const t = useTranslations();

  const methods = useForm<ResponseCenterAlarmCodeRuleDetails>({
    defaultValues: {
      requireAll: true,
      ...defaultValues,
    },
  });

  const {
    control,
    setValue,
    trigger,
    formState: { isSubmitting, errors },
    handleSubmit,
    reset,
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const saveInputsData = (
    name: "id" | "alarmCode" | "order" | "flags" | "requireAll",
    data: string,
  ) => {
    const clearInput = data.replace(/\D/g, "");
    setValue(name, clearInput ? +clearInput : undefined);
    trigger(name);
  };

  return (
    <PrimeModal
      withFooter
      withHeader
      header={labels.title}
      onClose={onClose}
      isOpen={isOpen}
      fixedWidth="848px"
      loading={isSubmitting || isLoading}
      submitBtn={{ text: labels.submit, onClick: handleSubmit(onSubmit) }}
      cancelBtn={{ text: t("Common:cancel"), onClick: onClose }}
    >
      <p className="grey-600">{labels.description}</p>

      <Spacer size={32} />

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <FormProvider {...methods}>
          <form>
            <Controller
              name="flags"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const length = field.value?.length ?? 0;
                const values =
                  length > 0
                    ? field.value?.split(",").map((stringValue) => stringValue)
                    : [];

                return (
                  <MultiCodeInput
                    required
                    label={t(
                      "AlarmReceptions:alarm_handling_alarm_code_rules_input_classifications",
                    )}
                    placeholder={t("Common:input_multi_code_placeholder")}
                    values={values}
                    setValues={(updated) => {
                      const value = updated.join(",");
                      setValue(field.name, value, { shouldDirty: true });
                    }}
                    validationError={
                      errors.flags && t("Errors:input_field_required")
                    }
                  />
                );
              }}
            />

            <Spacer size={16} />

            <Row align="start">
              <Column>
                <Controller
                  name="alarmCode"
                  control={control}
                  rules={{
                    required: true,
                    pattern: /-?\d+/,
                  }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      label={t(
                        "AlarmReceptions:alarm_handling_alarm_code_rules_input_alarm_code",
                      )}
                      placeholder={t("Common:integer_number")}
                      value={`${field.value ?? ""}`}
                      onChange={field.onChange}
                      validationError={
                        errors.alarmCode &&
                        (errors.alarmCode.type === "required"
                          ? t("Errors:input_field_required")
                          : t("Errors:minus_and_digits"))
                      }
                    />
                  )}
                />
              </Column>
              <Spacer size={32} />
              <Column>
                <Controller
                  name="order"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      label={t(
                        "AlarmReceptions:alarm_handling_alarm_code_rules_input_order",
                      )}
                      placeholder={t("Common:integer_number")}
                      value={`${field.value ?? ""}`}
                      onChange={(e) => {
                        saveInputsData(field.name, e.currentTarget.value);
                      }}
                      validationError={
                        errors.order && t("Errors:input_field_required")
                      }
                    />
                  )}
                />
              </Column>
            </Row>

            <Spacer size={16} />

            <Row align="start">
              <CheckboxControl
                name="requireAll"
                label={t(
                  "AlarmReceptions:alarm_handling_alarm_code_rules_input_require_all",
                )}
                description={t(
                  "AlarmReceptions:code_rules_require_all_description",
                )}
              />
            </Row>
          </form>
        </FormProvider>
      )}
    </PrimeModal>
  );
};
